import React, { useEffect, useState } from "react";
import { Globe, ShieldCheck, Trash2, Copy, Check, ExternalLink, AlertTriangle } from "lucide-react";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard,
  AppCardContent,
  AppCardHeader,
  AppCardTitle,
  AppButton,
  StatusBadge,
  ConfirmModal,
} from "../ui";

/**
 * Institute admin → Settings → Custom Domain
 *
 * Plan-gated by `customDomain`. The institute types the exact host where the
 * LMS should load (`lmsCustomHost`) — a subdomain (lms.foo.com) or the bare
 * apex (foo.com). When the public CMS website is ON, they also map the apex
 * (`customDomain`) to the marketing site. The verify step checks DNS; HTTPS is
 * provisioned server-side (ACM + CloudFront for the LMS, certbot for the CMS
 * apex). A manual Generate/Rebuild SSL action covers stuck/missing certs.
 */
const CmsCustomDomainSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Apex (public website) — only used when CMS website is enabled.
  const [customDomain, setCustomDomain] = useState("");
  const [savedDomain, setSavedDomain] = useState("");
  // The exact LMS host (authoritative).
  const [lmsCustomHost, setLmsCustomHost] = useState("");
  const [savedLmsHost, setSavedLmsHost] = useState("");

  const [domainVerified, setDomainVerified] = useState(false);
  const [cmsServerIp, setCmsServerIp] = useState("");
  const [cloudFrontDomain, setCloudFrontDomain] = useState("");
  const [cmsEnabled, setCmsEnabled] = useState(false);
  const [verifySteps, setVerifySteps] = useState([]);
  const [cmsCertStatus, setCmsCertStatus] = useState("");
  const [cmsCertIssuedAt, setCmsCertIssuedAt] = useState(null);
  const [cmsCertError, setCmsCertError] = useState("");
  const [lmsCertStatus, setLmsCertStatus] = useState("");
  const [lmsCloudFrontAdded, setLmsCloudFrontAdded] = useState(false);
  const [lmsAcmValidation, setLmsAcmValidation] = useState(null);
  const [hasCert, setHasCert] = useState(false);

  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [copiedKey, setCopiedKey] = useState("");
  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1500);
    } catch {
      showToast("Failed to copy", "error");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while either cert is being issued. Stops when both are settled.
  useEffect(() => {
    const isCmsPending = cmsCertStatus === "pending";
    const isLmsPending =
      lmsCertStatus === "pending" ||
      (domainVerified && lmsCertStatus && !lmsCloudFrontAdded && lmsCertStatus !== "failed");
    if (!isCmsPending && !isLmsPending) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await apiClient.get("/institute/cms-custom-domain");
        if (cancelled) return;
        setCmsCertStatus(data.cmsCertStatus || "");
        setCmsCertIssuedAt(data.cmsCertIssuedAt || null);
        setCmsCertError(data.cmsCertError || "");
        setLmsCertStatus(data.lmsCertStatus || "");
        setLmsCloudFrontAdded(!!data.lmsCloudFrontAdded);
        setLmsAcmValidation(data.lmsAcmValidation || null);
        setHasCert(!!data.hasCert);
      } catch {
        /* ignore — keep polling */
      }
    };
    const id = setInterval(tick, 7000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [cmsCertStatus, lmsCertStatus, lmsCloudFrontAdded, domainVerified]);

  const applyState = (data) => {
    setSavedDomain(data.customDomain || "");
    setCustomDomain(data.customDomain || "");
    setSavedLmsHost(data.lmsCustomHost || data.lmsHost || "");
    setLmsCustomHost(data.lmsCustomHost || data.lmsHost || "");
    setDomainVerified(!!data.domainVerified);
    setCmsServerIp(data.cmsServerIp || "");
    setCloudFrontDomain(data.cloudFrontDomain || "");
    setCmsEnabled(!!data.cmsEnabled);
    setCmsCertStatus(data.cmsCertStatus || "");
    setCmsCertIssuedAt(data.cmsCertIssuedAt || null);
    setCmsCertError(data.cmsCertError || "");
    setLmsCertStatus(data.lmsCertStatus || "");
    setLmsCloudFrontAdded(!!data.lmsCloudFrontAdded);
    setLmsAcmValidation(data.lmsAcmValidation || null);
    setHasCert(!!data.hasCert);
  };

  const load = async () => {
    try {
      const { data } = await apiClient.get("/institute/cms-custom-domain");
      applyState(data);
    } catch {
      showToast("Failed to load custom domain settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const nextHost = (lmsCustomHost || "").trim().toLowerCase();
    const nextApex = (customDomain || "").trim().toLowerCase();
    if (!nextHost) {
      showToast("Enter the address where your LMS should load.", "error");
      return;
    }
    setSaving(true);
    setVerifySteps([]);
    try {
      const body = { lmsCustomHost: nextHost };
      if (cmsEnabled) body.customDomain = nextApex;
      const { data } = await apiClient.put("/institute/cms-custom-domain", body);
      setSavedDomain(data.customDomain || "");
      setCustomDomain(data.customDomain || "");
      setSavedLmsHost(data.lmsCustomHost || "");
      setLmsCustomHost(data.lmsCustomHost || "");
      setDomainVerified(!!data.domainVerified);
      // Host change resets the cert server-side.
      setHasCert(false);
      setLmsCertStatus("");
      setLmsCloudFrontAdded(false);
      setLmsAcmValidation(null);
      showToast(data.message || "Saved.");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to save domain.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerifySteps([]);
    try {
      const { data } = await apiClient.post("/institute/cms-custom-domain/verify");
      setVerifySteps(data.steps || []);
      setDomainVerified(!!data.verified);
      if (data.verified && data.certProvisioning) {
        setCmsCertStatus("pending");
        setCmsCertError("");
      }
      // Refresh full state so ACM validation record + LMS cert status appear.
      try {
        const { data: fresh } = await apiClient.get("/institute/cms-custom-domain");
        setLmsCertStatus(fresh.lmsCertStatus || "");
        setLmsCloudFrontAdded(!!fresh.lmsCloudFrontAdded);
        setLmsAcmValidation(fresh.lmsAcmValidation || null);
        setHasCert(!!fresh.hasCert);
      } catch {
        /* ignore */
      }
      showToast(data.message || "Domain verified.");
    } catch (err) {
      setVerifySteps(err?.response?.data?.steps || []);
      showToast(err?.response?.data?.message || "Verification failed.", "error");
    } finally {
      setVerifying(false);
    }
  };

  const handleProvisionSsl = async () => {
    setProvisioning(true);
    try {
      const rebuild = lmsCertStatus === "pending" || lmsCertStatus === "failed";
      const { data } = await apiClient.post(
        "/institute/cms-custom-domain/provision-ssl",
        { rebuild },
      );
      setLmsCertStatus(data.lmsCertStatus || "pending");
      setLmsCloudFrontAdded(!!data.lmsCloudFrontAdded);
      if (data.lmsAcmValidation) setLmsAcmValidation(data.lmsAcmValidation);
      setHasCert(true);
      showToast(data.message || "SSL certificate requested.");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to provision SSL.", "error");
    } finally {
      setProvisioning(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await apiClient.put("/institute/cms-custom-domain", { customDomain: "", lmsCustomHost: "" });
      setSavedDomain("");
      setCustomDomain("");
      setSavedLmsHost("");
      setLmsCustomHost("");
      setDomainVerified(false);
      setVerifySteps([]);
      setHasCert(false);
      setLmsCertStatus("");
      setLmsCloudFrontAdded(false);
      setLmsAcmValidation(null);
      setConfirmOpen(false);
      showToast("Custom domain removed.");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to remove domain.", "error");
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <Shimmer height={132} count={1} />
    );
  }

  const ipMissing = !cmsServerIp;
  const ipDisplay = cmsServerIp || "(not configured — contact support)";
  const cfDisplay = cloudFrontDomain || "(not configured — contact support)";
  const apex = savedDomain || "yourdomain.com";
  const lmsHost = savedLmsHost || (lmsCustomHost || "").trim() || "lms.yourinstitute.com";
  // Apex-level LMS host (no subdomain) — can't use a plain CNAME at the registrar.
  const isApexLms = !!savedLmsHost && savedLmsHost.split(".").length <= 2;

  const step1Done = !!savedLmsHost;
  const step3Done = domainVerified;

  const dirty =
    (lmsCustomHost || "").trim().toLowerCase() !== (savedLmsHost || "").toLowerCase() ||
    (cmsEnabled &&
      (customDomain || "").trim().toLowerCase() !== (savedDomain || "").toLowerCase());

  const saveDisabled =
    saving || !lmsCustomHost.trim() || (cmsEnabled && !customDomain.trim()) || !dirty;

  // Show the manual SSL action when DNS is verified but HTTPS isn't live yet.
  const showSslAction =
    domainVerified &&
    !lmsCloudFrontAdded &&
    (!hasCert || lmsCertStatus === "pending" || lmsCertStatus === "failed");
  const sslActionLabel = hasCert ? "Rebuild SSL certificate" : "Generate SSL certificate";

  const renderDnsRow = (key, type, host, value, { disableCopy = false } = {}) => (
    <div className="cms-cd__dns-row">
      <span className="cms-cd__dns-type">{type}</span>
      <div className="cms-cd__dns-cells">
        <div>
          <span className="cms-cd__dns-label">Host / Name</span>
          <code className="cms-cd__dns-value">{host}</code>
        </div>
        <div>
          <span className="cms-cd__dns-label">Points to / Value</span>
          <code className="cms-cd__dns-value">{value}</code>
          <button
            type="button"
            className="cms-cd__copy-btn"
            onClick={() => copy(key, value)}
            title="Copy"
            disabled={disableCopy}
          >
            {copiedKey === key ? <Check size={13} /> : <Copy size={13} />}
            {copiedKey === key ? "Copied" : "Copy"}
          </button>
        </div>
        <div>
          <span className="cms-cd__dns-label">TTL</span>
          <code className="cms-cd__dns-value">300</code>
        </div>
      </div>
    </div>
  );

  return (
    <div className="cms-custom-domain-settings">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <Globe size={16} /> Custom Domain
          </AppCardTitle>
          <p className="settings-card-desc">
            Run your LMS on your own domain (e.g.{" "}
            <strong>lms.yourinstitute.com</strong>). Follow the 3 steps below.
          </p>
        </AppCardHeader>
        <AppCardContent>
          {/* ── How it works ────────────────────────────────────────────── */}
          <div className="cms-cd__intro">
            <p>
              <strong>How your domain will be used:</strong>
            </p>
            <ul>
              {cmsEnabled ? (
                <li>
                  <strong>{apex}</strong> &amp; <strong>www.{apex}</strong> →
                  your <em>public website</em> (CMS home page).
                </li>
              ) : (
                <li>
                  Public CMS website is <strong>off</strong>. Your domain points
                  straight to the <em>LMS panel</em>. (Turn the website on from
                  the <strong>Website</strong> tab if you also want a marketing
                  site on your root domain.)
                </li>
              )}
              <li>
                <strong>{lmsHost}</strong> → your <em>LMS panel</em> (login,
                courses, batches, etc.).
              </li>
            </ul>
          </div>

          {/* ── Status banner once saved ───────────────────────────────── */}
          {savedLmsHost && (
            <div className="cms-cd__status-banner">
              <div>
                <span className="cms-cd__status-label">LMS address</span>
                <a
                  href={`https://${savedLmsHost}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cms-cd__status-domain"
                >
                  {savedLmsHost} <ExternalLink size={12} />
                </a>
              </div>
              <StatusBadge status={domainVerified ? "active" : "pending"}>
                {domainVerified ? "Verified & Live" : "Pending Verification"}
              </StatusBadge>
            </div>
          )}

          {/* ── Step 1: Enter domain ───────────────────────────────────── */}
          <div className={`cms-cd__step ${step1Done ? "cms-cd__step--done" : ""}`}>
            <div className="cms-cd__step-num">1</div>
            <div className="cms-cd__step-body">
              <h4 className="cms-cd__step-title">
                {cmsEnabled ? "Enter your website & LMS addresses" : "Enter your LMS address"}
              </h4>
              <p className="cms-cd__step-desc">
                {cmsEnabled
                  ? "Your root domain serves the public website; type a separate address for the LMS panel."
                  : "Type the exact address where you want the LMS to load — a subdomain like lms.yourinstitute.com, or your root domain."}
              </p>

              {cmsEnabled && (
                <div className="cms-cd__field">
                  <label className="cms-cd__field-label">Public website domain</label>
                  <input
                    type="text"
                    className="cms-cd__input"
                    placeholder="yourinstitute.com"
                    value={customDomain}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustomDomain(v);
                      // Smart default for the LMS host while it's untouched.
                      const apexVal = v.trim().toLowerCase();
                      if (!lmsCustomHost.trim() && apexVal) {
                        setLmsCustomHost(`lms.${apexVal}`);
                      }
                    }}
                    disabled={saving}
                  />
                </div>
              )}

              <div className="cms-cd__field">
                <label className="cms-cd__field-label">LMS address</label>
                <input
                  type="text"
                  className="cms-cd__input"
                  placeholder="lms.yourinstitute.com"
                  value={lmsCustomHost}
                  onChange={(e) => setLmsCustomHost(e.target.value)}
                  disabled={saving}
                />
                <p className="cms-cd__hint">
                  Your LMS will load at{" "}
                  <code>https://{(lmsCustomHost || "").trim() || "lms.yourinstitute.com"}</code>.
                  No <code>http://</code>, no <code>/</code>.
                </p>
              </div>

              <div className="cms-cd__step-row">
                <AppButton variant="default" onClick={handleSave} disabled={saveDisabled}>
                  {saving ? "Saving…" : savedLmsHost ? "Update" : "Save"}
                </AppButton>
              </div>
            </div>
          </div>

          {/* ── Step 2: Add DNS records ────────────────────────────────── */}
          <div className={`cms-cd__step ${!step1Done ? "cms-cd__step--locked" : ""}`}>
            <div className="cms-cd__step-num">2</div>
            <div className="cms-cd__step-body">
              <h4 className="cms-cd__step-title">Add these DNS records</h4>
              <p className="cms-cd__step-desc">
                Log in to your domain registrar's DNS panel and add the
                following records. (Delete any existing records on the same Host
                first.)
              </p>
              {ipMissing && cmsEnabled && (
                <div className="cms-cd__warn">
                  Server IP isn't configured yet. Please contact support — we
                  need to set this up before you can add the A record.
                </div>
              )}
              <div className="cms-cd__dns">
                {cmsEnabled && renderDnsRow("a-apex", "A", "@", ipDisplay, { disableCopy: ipMissing })}
                {cmsEnabled && renderDnsRow("cname-www", "CNAME", "www", apex)}

                {isApexLms ? (
                  <div className="cms-cd__warn">
                    <AlertTriangle size={14} />{" "}
                    <span>
                      <strong>{lmsHost}</strong> is a root (apex) domain, which
                      can't use a plain <code>CNAME</code>. Point it to{" "}
                      <code>{cfDisplay}</code> using your registrar's{" "}
                      <strong>ALIAS</strong>/<strong>ANAME</strong> record (or
                      Cloudflare "CNAME flattening"). If your registrar doesn't
                      support those, use a subdomain like{" "}
                      <code>lms.{lmsHost}</code> instead.
                    </span>
                  </div>
                ) : (
                  renderDnsRow("cname-lms", "CNAME", lmsHost, cfDisplay, { disableCopy: !cloudFrontDomain })
                )}

                {lmsAcmValidation && lmsAcmValidation.name && !lmsCloudFrontAdded && (
                  <>
                    <p className="cms-cd__hint" style={{ marginTop: "0.5rem" }}>
                      <strong>One more record</strong> — to enable HTTPS for{" "}
                      <code>{lmsHost}</code>, AWS needs to validate ownership.
                      Add this CNAME at your registrar (keep it for automatic
                      renewal):
                    </p>
                    {renderDnsRow(
                      "cname-acm",
                      "CNAME",
                      lmsAcmValidation.name.replace(/\.$/, ""),
                      lmsAcmValidation.value.replace(/\.$/, ""),
                    )}
                  </>
                )}
              </div>
              <p className="cms-cd__hint">
                DNS changes typically propagate in 5–30 minutes (sometimes up
                to a few hours). You can check propagation at{" "}
                <a
                  href={`https://dnschecker.org/#CNAME/${lmsHost}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  dnschecker.org
                </a>
                .
              </p>
            </div>
          </div>

          {/* ── Step 3: Verify ─────────────────────────────────────────── */}
          <div
            className={`cms-cd__step ${
              !step1Done ? "cms-cd__step--locked" : ""
            } ${step3Done ? "cms-cd__step--done" : ""}`}
          >
            <div className="cms-cd__step-num">3</div>
            <div className="cms-cd__step-body">
              <h4 className="cms-cd__step-title">Verify &amp; activate</h4>
              <p className="cms-cd__step-desc">
                Once DNS has propagated, click below. We'll check that your
                records point to our servers, then activate HTTPS for your site.
              </p>
              <div className="cms-cd__step-row">
                <AppButton
                  variant="default"
                  onClick={handleVerify}
                  disabled={!step1Done || verifying}
                >
                  <ShieldCheck size={14} />
                  {verifying ? "Verifying…" : domainVerified ? "Re-verify DNS" : "Verify DNS"}
                </AppButton>
                {showSslAction && (
                  <AppButton
                    variant="outline"
                    onClick={handleProvisionSsl}
                    disabled={provisioning}
                  >
                    <ShieldCheck size={14} />
                    {provisioning ? "Working…" : sslActionLabel}
                  </AppButton>
                )}
                {savedLmsHost && (
                  <AppButton
                    variant="destructive"
                    onClick={() => setConfirmOpen(true)}
                    disabled={removing}
                  >
                    <Trash2 size={14} />
                    Remove Domain
                  </AppButton>
                )}
              </div>

              {verifySteps.length > 0 && (
                <div
                  className={`cms-cd__verify-result ${
                    domainVerified ? "cms-cd__verify-result--ok" : "cms-cd__verify-result--fail"
                  }`}
                >
                  <strong>
                    {domainVerified ? "✓ Verification passed" : "✗ Verification failed"}
                  </strong>
                  <ul>
                    {verifySteps.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                  {!domainVerified && (
                    <p className="cms-cd__hint">
                      DNS may still be propagating. Wait a few minutes and try again.
                    </p>
                  )}
                </div>
              )}

              {domainVerified && cmsEnabled && cmsCertStatus && (
                <div
                  className={`cms-cd__verify-result ${
                    cmsCertStatus === "issued"
                      ? "cms-cd__verify-result--ok"
                      : cmsCertStatus === "failed"
                        ? "cms-cd__verify-result--fail"
                        : ""
                  }`}
                >
                  <strong>
                    {cmsCertStatus === "issued" && "✓ Website HTTPS active"}
                    {cmsCertStatus === "pending" && "⏳ Issuing website HTTPS certificate…"}
                    {cmsCertStatus === "failed" && "✗ Website HTTPS provisioning failed"}
                  </strong>
                  {cmsCertStatus === "issued" && (
                    <p className="cms-cd__hint">
                      Your site is live at{" "}
                      <a href={`https://${savedDomain}`} target="_blank" rel="noreferrer">
                        https://{savedDomain}
                      </a>
                      {cmsCertIssuedAt && (
                        <> · issued {new Date(cmsCertIssuedAt).toLocaleString()}</>
                      )}
                    </p>
                  )}
                  {cmsCertStatus === "pending" && (
                    <p className="cms-cd__hint">
                      Usually takes under a minute. This page will update automatically.
                    </p>
                  )}
                  {cmsCertStatus === "failed" && (
                    <>
                      <p className="cms-cd__hint">
                        We couldn't issue a Let's Encrypt certificate. The most common cause
                        is DNS not yet pointing to our server, or rate limits. Try again in
                        a few minutes by clicking <strong>Re-verify DNS</strong>.
                      </p>
                      {cmsCertError && (
                        <p className="cms-cd__hint">
                          <code>{cmsCertError.slice(0, 240)}</code>
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {domainVerified && (lmsCertStatus || hasCert) && (
                <div
                  className={`cms-cd__verify-result ${
                    lmsCloudFrontAdded
                      ? "cms-cd__verify-result--ok"
                      : lmsCertStatus === "failed"
                        ? "cms-cd__verify-result--fail"
                        : ""
                  }`}
                >
                  <strong>
                    {lmsCloudFrontAdded && `✓ LMS HTTPS active`}
                    {!lmsCloudFrontAdded && lmsCertStatus === "pending" && `⏳ LMS HTTPS — waiting for AWS validation`}
                    {!lmsCloudFrontAdded && lmsCertStatus === "failed" && `✗ LMS HTTPS provisioning failed`}
                    {!lmsCloudFrontAdded && !lmsCertStatus && `⏳ LMS HTTPS — not requested yet`}
                  </strong>
                  {lmsCloudFrontAdded && (
                    <p className="cms-cd__hint">
                      Your LMS is live at{" "}
                      <a href={`https://${lmsHost}`} target="_blank" rel="noreferrer">
                        https://{lmsHost}
                      </a>
                    </p>
                  )}
                  {!lmsCloudFrontAdded && lmsCertStatus === "pending" && (
                    <p className="cms-cd__hint">
                      Add the <strong>ACM CNAME</strong> shown in step 2 above (the
                      record starting with <code>_</code>). AWS issues the
                      certificate automatically once it propagates — usually
                      within 5–30 minutes — then attaches it to {lmsHost}.
                    </p>
                  )}
                  {!lmsCloudFrontAdded && (lmsCertStatus === "failed" || !lmsCertStatus) && (
                    <p className="cms-cd__hint">
                      Click <strong>{sslActionLabel}</strong> above to (re)issue
                      the certificate.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      <ConfirmModal
        show={confirmOpen}
        title="Remove custom domain?"
        message={`This will stop serving your LMS at "${savedLmsHost}". You can add it back later.`}
        confirmText="Remove"
        variant="danger"
        onConfirm={handleRemove}
        onHide={() => setConfirmOpen(false)}
        loading={removing}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default CmsCustomDomainSettings;
